import {
  CheckOutlined,
  CloseOutlined,
  Delete,
  Edit,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import OverflowToolTip from '../../../../common-components/overflow-tooltip/overflow-tooltip';
import { isValidEmail } from './pro-alert-helpers';

interface EmailListItemProps {
  email: string;
  emails: string[];
  editing: boolean;
  onStartEdit: () => void;
  onUpdate: (newEmail: string) => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
}

function EmailListItem({
  email,
  emails,
  editing,
  onStartEdit,
  onUpdate,
  onCancelEdit,
  onDelete,
  disabled,
}: EmailListItemProps) {
  const [input, setInput] = useState(email);
  const ACTIONS_WIDTH = '5.8rem'; //  3.5rem for 1, 5.8rem for 2, 8rem for 3 buttons

  const isDuplicateEmail = (newEmail: string) =>
    emails
      .filter((e) => e !== email)
      .some((e) => e.toLowerCase() === newEmail.toLowerCase());

  const helperText = (newInput: string) => {
    if (!isValidEmail(newInput)) return 'Invalid email';
    if (isDuplicateEmail(newInput)) return 'Email already exists';
    return '';
  };

  const handleEditClick = () => {
    if (!disabled) {
      onStartEdit();
      setInput(email); // Always reset input to current email
    }
  };

  const handleSaveChanges = () => {
    if (isValidEmail(input) && !isDuplicateEmail(input)) {
      onUpdate(input);
    }
  };

  const handleCancelEdit = () => {
    onCancelEdit();
    setInput(email);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <ListItem
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          minHeight: 40,
          maxHeight: 40,
          minWidth: 0,
          pl: 1,
          pr: ACTIONS_WIDTH,
          flex: 1,
        }}
      >
        {editing ? (
          <TextField
            value={input}
            onChange={(e) => setInput(e.target.value)}
            error={
              (!!input && !isValidEmail(input)) ||
              (!!input && isDuplicateEmail(input))
            }
            label={helperText(input)}
            disabled={disabled}
            sx={{
              width: '100%',
              minWidth: 0,
              '.MuiInputBase-root': { height: 32 },
            }}
            inputProps={{
              style: { padding: '8px 6px' },
              'data-testid': `existing-email-update-input-${email}`,
            }}
            type="email"
          />
        ) : (
          <OverflowToolTip watchKey={email}>
            <ListItemText
              primary={email}
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                flex: 1,
                minHeight: 32,
                pr: ACTIONS_WIDTH,
                minWidth: 0,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={handleEditClick}
              data-testid={`pro-alert-email-item-${email}`}
              aria-label={`PRO Alert Email Item ${email}`}
            />
          </OverflowToolTip>
        )}
      </ListItem>
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '2px',
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="pro-alert-email-item-actions"
        aria-label="Pro Alert Email Item Actions"
      >
        {editing ? (
          <>
            <IconButton
              size="small"
              onClick={handleSaveChanges}
              disabled={
                disabled || !isValidEmail(input) || isDuplicateEmail(input)
              }
              aria-label="check"
              data-testid={`pro-alert-email-check-button-${email}`}
            >
              <CheckOutlined />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleCancelEdit}
              disabled={disabled}
              aria-label="cancel"
              data-testid={`pro-alert-email-cancel-button-${email}`}
            >
              <CloseOutlined />
            </IconButton>
          </>
        ) : (
          <>
            <IconButton
              size="small"
              aria-label="edit"
              onClick={handleEditClick}
              disabled={disabled}
              data-testid={`pro-alert-email-edit-button-${email}`}
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              aria-label="delete"
              onClick={onDelete}
              disabled={disabled}
              data-testid={`pro-alert-email-delete-button-${email}`}
            >
              <Delete />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );
}

export default EmailListItem;

EmailListItem.defaultProps = {
  disabled: false,
};
